import { AnalyseResponse, GeneratedEmailResponse, PanelView } from "../types";
import { getSetting, setSetting, removeSetting } from "../office/storage";

// Persists lightweight UI state across add-in reloads.
//
// In Outlook on the web, pinning the pane reloads the add-in iframe (and so does
// switching messages). That remounts React and would otherwise drop the user back
// to Home, losing any analysis/result they were viewing. We stash the current
// view + results, keyed to the messageId they belong to, and restore them on
// reload — but ONLY when the same message is still selected, so a different email
// never shows another email's results.
//
// Storage uses roamingSettings (see ../office/storage) rather than sessionStorage,
// because OWA recreates the iframe on reload and wipes sessionStorage. A short TTL
// keeps us restoring across genuine reloads (seconds apart) without resurrecting
// stale state when the add-in is reopened much later — roamingSettings, unlike
// sessionStorage, persists across sessions.

const KEY = "aslan_ui_state";

// Views worth restoring after a reload. Transient views (login, loading, and
// mid-flow wizard/entry screens) are intentionally excluded — re-entering them
// on reload would be wrong or confusing.
const RESTORABLE: PanelView[] = ["results", "writeEmailResults"];

// Only restore state saved within this window.
const MAX_AGE_MS = 5 * 60 * 1000;

export interface PersistedUiState {
  view: PanelView;
  // The Outlook messageId the persisted result belongs to. null for results
  // not tied to the open message (e.g. manual-entry analysis), which are not
  // restored against a real message.
  messageId: string | null;
  analysisResult: AnalyseResponse | null;
  analysisSnapshot: { body: string; subject: string } | null;
  writeEmailResult: GeneratedEmailResponse | null;
}

interface StoredUiState extends PersistedUiState {
  savedAt: number;
}

export async function saveUiState(state: PersistedUiState): Promise<void> {
  // Anything that isn't a restorable result view means "nothing to restore" —
  // clear so a later reload on the same message doesn't resurrect stale state.
  if (!RESTORABLE.includes(state.view)) {
    await removeSetting(KEY);
    return;
  }
  const stored: StoredUiState = { ...state, savedAt: Date.now() };
  await setSetting(KEY, stored);
}

// Returns the persisted state only if it targets `messageId`, points at a
// restorable view, and is recent; otherwise null (caller falls back to Home).
export function loadUiState(messageId: string | null): PersistedUiState | null {
  const parsed = getSetting<StoredUiState>(KEY);
  if (!parsed) return null;
  if (!RESTORABLE.includes(parsed.view)) return null;
  if (parsed.messageId !== messageId) return null;
  if (typeof parsed.savedAt !== "number" || Date.now() - parsed.savedAt > MAX_AGE_MS) {
    return null;
  }
  return parsed;
}

export async function clearUiState(): Promise<void> {
  await removeSetting(KEY);
}
